import { ZoomInMap as IconZoomInMap } from "@mui/icons-material";
import { Grow, Paper, Popper } from "@mui/material";
import { bbox, circle } from "@turf/turf";
import { LngLatBoundsLike, PaddingOptions } from "mapbox-gl";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useGaiaMap } from "~/modules/map/hooks/useGaiaMap";

import { MAP_ANIMATE_OPTIONS, MAP_CENTER_ADDRESS_RADIUS_KM, MAP_PADDING, NETHERLANDS_BOUNDS } from "../../mapConstants";
import { MapButton } from "../MapButton";
import { MapCenterAddressPanel } from "./MapCenterAddressPanel";
import { MapCenterOptionsMenu } from "./MapCenterOptionsMenu";
import { MapBounds, MapCenterAddress } from "./types";
import { useMapCenterPreference } from "./useMapCenterPreference";

type MapButtonFitBoundsProps = {
    bounds: MapBounds | null;
    padding?: PaddingOptions;
};

export function MapButtonFitBounds({ bounds, padding = {} }: MapButtonFitBoundsProps) {
    const { t } = useTranslation("map");
    const { map } = useGaiaMap();
    const { mode, address, selectBounds, selectAddress } = useMapCenterPreference();

    const [optionsAnchorEl, setOptionsAnchorEl] = useState<HTMLElement | null>(null);
    const [optionsPanel, setOptionsPanel] = useState<"modeSelect" | "addressEdit">("modeSelect");

    const targetBounds = bounds ?? NETHERLANDS_BOUNDS;
    const activeAddress = mode === "address" ? address : null;

    const fitBounds = (target: GeoJSON.BBox) => {
        map?.fitBounds(target as LngLatBoundsLike, { padding: { ...MAP_PADDING, ...padding }, ...MAP_ANIMATE_OPTIONS });
    };
    const centerOnAddress = (target: MapCenterAddress) => {
        const location = [target.longitude, target.latitude];
        fitBounds(bbox(circle(location, MAP_CENTER_ADDRESS_RADIUS_KM, { units: "kilometers" })));
    };

    return (
        <>
            <MapButton
                onClick={() => (activeAddress ? centerOnAddress(activeAddress) : fitBounds(targetBounds.bbox))}
                icon={<IconZoomInMap />}
                label={t(`mapButtonFitBounds.centerOn.${activeAddress ? "address" : targetBounds.kind}`)}
                caption={activeAddress?.shortAddress}
                onChevronClick={event => {
                    if (optionsAnchorEl) {
                        setOptionsAnchorEl(null);
                        return;
                    }
                    setOptionsPanel("modeSelect");
                    setOptionsAnchorEl(event.currentTarget);
                }}
                isChevronOpen={optionsAnchorEl !== null}
                chevronLabel={t("mapButtonFitBounds.openOptions")}
            />

            <Popper
                open={optionsAnchorEl !== null}
                anchorEl={optionsAnchorEl}
                placement="top-end"
                transition
                modifiers={[{ name: "offset", options: { offset: [8, 4] } }]}
            >
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps} style={{ transformOrigin: "right bottom" }}>
                        <Paper role="dialog" aria-label={t("mapButtonFitBounds.openOptions")} sx={{ width: 304 }}>
                            {optionsPanel === "modeSelect" ? (
                                <MapCenterOptionsMenu
                                    mode={mode}
                                    boundsKind={targetBounds.kind}
                                    address={address}
                                    onSelectBounds={() => {
                                        selectBounds();
                                        fitBounds(targetBounds.bbox);
                                        setOptionsAnchorEl(null);
                                    }}
                                    onSelectAddress={selectedAddress => {
                                        selectAddress(selectedAddress);
                                        centerOnAddress(selectedAddress);
                                        setOptionsAnchorEl(null);
                                    }}
                                    onEditAddress={() => setOptionsPanel("addressEdit")}
                                />
                            ) : (
                                <MapCenterAddressPanel
                                    address={address}
                                    onBack={() => setOptionsPanel("modeSelect")}
                                    onCenter={selectedAddress => {
                                        selectAddress(selectedAddress);
                                        centerOnAddress(selectedAddress);
                                        setOptionsAnchorEl(null);
                                    }}
                                />
                            )}
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
}
